import { Heading, Separator, toast } from "@overmap-ai/blocks"
import { type IssueType, OrganizationAccessLevel, type Stored, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectIssueTypeById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useUserOrganizationAccess } from "../../hooks/users"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import {
	BASE_EXTENSIONS,
	IssueTypeAttachmentNode,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { EditorToolbar, NewLineFloatingMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { IssueTypeFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/general")

export const IssueTypeSettingsGeneralView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchIssueType = useCallback(
		(fields: Partial<Stored<IssueType>>) => {
			const [_, promise] = sdk.issueTypes.update({ uuid: issueType.uuid, ...fields })

			promise.catch((error) => {
				toast.error({
					title: "Failed to update issue type",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueType.uuid],
	)

	const handleDescriptionChange = useCallback(
		(description: JSONContent | undefined) => {
			handlePatchIssueType({ description: description ? JSON.stringify(description) : undefined })
		},
		[handlePatchIssueType],
	)

	const handleAttachFilesToIssueType = useCallback(
		(files: File[]) => {
			return sdk.issueTypeAttachments.bulkAdd(
				files.map((file) => ({
					issue_type: issueType.uuid,
					file,
				})),
			)
		},
		[issueType.uuid],
	)

	const editorContent = useEditorContent(issueType.description)
	const handleEditorBlur = useEditorBlur(editorContent, handleDescriptionChange)

	const editor = useEditor(
		{
			editorProps: {
				attributes: { class: "outline-none" },
			},
			content: editorContent,
			editable: isOrganizationAdmin,
			onBlur: handleEditorBlur,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				IssueTypeAttachmentNode,
				IssueTypeFileHandler.configure({
					uuid: issueType.uuid,
				}),
				Placeholder.configure({
					placeholder: "Enter description",
				}),
			],
		},
		[issueType.uuid, handlePatchIssueType],
	)

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFilesToIssueType(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFilesToIssueType],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">General</Heading>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-hidden">
					<EditorContext value={editor}>
						<EditorToolbar editor={editor} />
						<EditorContent className="w-full grow overflow-y-auto overflow-x-hidden" editor={editor} />
						<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
					</EditorContext>
				</div>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsGeneralView.displayName = "IssueTypeSettingsGeneralView"
